// Admin Newsletter: subscribers list + campaign editor + monthly thematic
// composer with scheduling queue.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mail, Download, RotateCcw, Send, FileText, ShoppingBag, GraduationCap, Loader2, Trash2, Plus, X, Check, Calendar, MapPin, Sparkles, Upload, UserPlus, FileSpreadsheet, Search } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { apiFetch } from "@/lib/api";
import SpamPurgeButton from "@/components/admin/SpamPurgeButton";

const TABS = [
  { id: "subscribers", label: "Abonnés" },
  { id: "thematic", label: "Thématique mensuelle" },
  { id: "drafts", label: "Brouillons" },
  { id: "history", label: "Historique" },
];

export default function AdminNewsletterPage() {
  const [tab, setTab] = useState("subscribers");
  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10" data-testid="admin-newsletter">
      <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-6">Newsletter</h1>
      <div className="flex gap-2 border-b border-[color:var(--gv-line)] mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? "border-[color:var(--gv-pink)] text-[color:var(--gv-ink)] font-medium"
                : "border-transparent text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]"
            }`}
            data-testid={`newsletter-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "subscribers" && <SubscribersPanel />}
      {tab === "thematic" && <ThematicPanel />}
      {tab === "drafts" && <DraftsPanel />}
      {tab === "compose" && <ComposePanel />}
      {tab === "history" && <HistoryPanel />}
    </div>
  );
}

// ---------- Drafts tab — monthly-theme newsletter drafts (4 langs) ----------
function DraftsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoadingState, setPreviewLoadingState] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(null); // {bundle_id, langs:[], sendAt}
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await adminApi.newsletterDraftsList();
      setItems(r?.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Group drafts by bundle_id for a cleaner UI
  const groups = useMemo(() => {
    const m = new Map();
    for (const d of items) {
      if (!m.has(d.bundle_id)) m.set(d.bundle_id, { bundle_id: d.bundle_id, theme: d.theme, updated_at: d.updated_at, langs: [] });
      m.get(d.bundle_id).langs.push(d);
    }
    return Array.from(m.values()).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
  }, [items]);

  const openPreview = async (bundle_id, language) => {
    setPreviewLoadingState(true); setPreviewHtml(""); setError("");
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      let token = null;
      try { token = JSON.parse(localStorage.getItem("geovinum.auth.v1"))?.token; } catch { /* no token */ }
      const r = await fetch(`${base}/api/admin/newsletter-drafts/${bundle_id}/${language}/preview`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}`, "X-Admin-Key": token } : {}),
        },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const html = await r.text();
      setPreviewHtml(html);
    } catch (e) { setError(e.message); setPreviewHtml(null); }
    finally { setPreviewLoadingState(false); }
  };

  const openSchedule = (bundle_id, langs) => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setScheduleModal({ bundle_id, langs: new Set(langs.map((l) => l.language)), sendAt: local, available: langs.map((l) => l.language) });
  };

  const confirmSchedule = async () => {
    if (!scheduleModal) return;
    setBusy(true); setError("");
    try {
      const body = {
        send_at: new Date(scheduleModal.sendAt).toISOString(),
        languages: Array.from(scheduleModal.langs),
      };
      await adminApi.newsletterDraftSchedule(scheduleModal.bundle_id, body);
      setScheduleModal(null);
      await reload();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const deleteDraft = async (bundle_id, language) => {
    if (!window.confirm(`Supprimer le brouillon ${language.toUpperCase()} ?`)) return;
    try {
      await adminApi.newsletterDraftDelete(bundle_id, language);
      await reload();
    } catch (e) { setError(e.message); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[color:var(--gv-pink)]" /></div>;

  return (
    <div className="space-y-4" data-testid="newsletter-drafts-panel">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-[5px] text-[13px]">{error}</div>}
      {groups.length === 0 && (
        <div className="text-center py-12 text-[color:var(--gv-ink)]/55 text-[13.5px]">
          Aucun brouillon. Génère un Thème du mois pour en créer un.
        </div>
      )}
      {groups.map((g) => (
        <div key={g.bundle_id} className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-4" data-testid={`draft-group-${g.bundle_id}`}>
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-0.5">Thème du mois · brouillon</div>
              <div className="font-display text-xl truncate">{g.theme}</div>
              <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 mt-0.5">bundle {g.bundle_id} · maj {g.updated_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
            <button
              onClick={() => openSchedule(g.bundle_id, g.langs)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[color:var(--gv-pink)] text-white rounded-[5px] text-[13px] hover:opacity-90"
              data-testid={`draft-schedule-${g.bundle_id}`}
            >
              <Calendar size={13} /> Programmer
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {g.langs.map((d) => (
              <div key={d.language} className="border border-[color:var(--gv-line)] rounded-[5px] p-3 flex items-center gap-2">
                <span className="inline-block uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded border border-[color:var(--gv-pink)] text-[color:var(--gv-pink)]">{d.language}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] truncate">{d.subject}</div>
                  <div className="text-[11px] text-[color:var(--gv-ink)]/55 truncate">{d.intro}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openPreview(d.bundle_id, d.language)} className="inline-flex items-center gap-1 px-2 py-1 text-[11.5px] border border-[color:var(--gv-line)] rounded hover:border-[color:var(--gv-pink)]">
                    Prévisualiser
                  </button>
                  <button onClick={() => deleteDraft(d.bundle_id, d.language)} className="inline-flex items-center gap-1 px-2 py-1 text-[11.5px] border border-red-200 text-red-600 rounded hover:bg-red-50">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Preview modal */}
      {previewHtml !== null && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="draft-preview-modal">
          <div className="bg-white rounded-[6px] max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[color:var(--gv-line)]">
              <div className="text-[12.5px]">Prévisualisation newsletter</div>
              <button onClick={() => setPreviewHtml(null)}><X size={16} /></button>
            </div>
            {previewLoadingState ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin" /></div>
            ) : (
              <iframe srcDoc={previewHtml} title="preview" className="flex-1 w-full border-0" />
            )}
          </div>
        </div>
      )}

      {/* Schedule modal */}
      {scheduleModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="draft-schedule-modal">
          <div className="bg-white rounded-[6px] p-6 max-w-md w-full shadow-xl">
            <h3 className="font-display text-xl mb-3">Programmer l'envoi</h3>
            <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mb-4">
              Les brouillons sélectionnés seront déplacés dans la file d'envoi aux abonnés.
            </p>
            <div className="mb-4">
              <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1.5">Langues</label>
              <div className="flex gap-2 flex-wrap">
                {scheduleModal.available.map((lg) => {
                  const on = scheduleModal.langs.has(lg);
                  return (
                    <button
                      key={lg}
                      onClick={() => {
                        const n = new Set(scheduleModal.langs);
                        if (on) n.delete(lg); else n.add(lg);
                        setScheduleModal({ ...scheduleModal, langs: n });
                      }}
                      className={`px-3 py-1.5 text-[12px] rounded border ${on ? "bg-[color:var(--gv-pink)] text-white border-[color:var(--gv-pink)]" : "bg-white border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/70"}`}
                      data-testid={`schedule-lang-${lg}`}
                    >
                      {lg.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mb-5">
              <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1.5">Date & heure</label>
              <input
                type="datetime-local"
                value={scheduleModal.sendAt}
                onChange={(e) => setScheduleModal({ ...scheduleModal, sendAt: e.target.value })}
                className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-[14px]"
                data-testid="schedule-send-at"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setScheduleModal(null)} className="px-4 py-2 rounded-[5px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)]">Annuler</button>
              <button
                onClick={confirmSchedule}
                disabled={busy || scheduleModal.langs.size === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] hover:opacity-90 disabled:opacity-40"
                data-testid="schedule-confirm"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Programmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Thematic (per-row planner: datetime · theme · product · preview · test · signature) ----------
// Builds an editable table of the next N newsletters. Each row can be
// previewed (AI intro/conclusion) or test-sent to a QA email. "Enregistrer
// la planification" persists the whole table via `schedule-bulk`.
function ThematicPanel() {
  const [themes, setThemes] = useState([]);
  const [products, setProducts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [appFeatures, setAppFeatures] = useState([]); // [{module, items:[...]}]
  const [rows, setRows] = useState([]); // {send_at, theme_slug, product_id?}
  const [scheduled, setScheduled] = useState([]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [previewIdx, setPreviewIdx] = useState(null); // which row's preview is open
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmailByRow, setTestEmailByRow] = useState({});
  // Rich narrative editor state — one editing row at a time.
  const [editIdx, setEditIdx] = useState(null);
  const [editDraft, setEditDraft] = useState(null); // {narratives, suggested_product, cepages, producers, ...}
  const [editBusy, setEditBusy] = useState(false);
  const [editPreviewHtml, setEditPreviewHtml] = useState("");

  useEffect(() => {
    adminApi.newsletterThematicThemes().then((d) => setThemes(d?.items || []));
    adminApi.newsletterThematicProducts().then((d) => setProducts(d?.items || []));
    adminApi.newsletterThematicCourses().then((d) => setCourses(d?.items || []));
    adminApi.newsletterThematicAppFeatures().then((d) => setAppFeatures(d?.groups || []));
    adminApi.newsletterThematicList().then((d) => setScheduled(d?.items || []));
  }, []);

  // Hydrate the rows from the currently-queued schedule so the operator
  // can edit an in-flight planning instead of starting from a blank slate.
  useEffect(() => {
    if (rows.length === 0 && scheduled.length > 0) {
      const queued = scheduled.filter((s) => s.status === "queued");
      if (queued.length > 0) {
        setRows(queued.map((s) => ({
          id: s.id,
          send_at: (s.send_at || `${s.send_date}T09:00:00Z`).slice(0, 16),
          theme_slug: s.theme_slug || "",
          product_id: s.product_id || "",
          course_id: s.course_id || "",
          narratives: s.narratives || null,
          podcasts: s.podcasts || [],
          feature_id: s.feature_id || "",
          cepage_slugs: s.cepage_slugs || [],
          producer_slugs: s.producer_slugs || [],
          fiche_descriptions: s.fiche_descriptions || {},
        })));
      }
    }
  }, [scheduled, rows.length]);

  // Group products by region_label for optgroup dropdowns → the operator
  // scans "Bourgogne / Bordeaux / Loire…" instead of an alphabetical soup.
  // Backend returns `region` (aliased from `region_label`) — fall through
  // to `region_slug` if neither is set.
  const productsByRegion = useMemo(() => {
    const groups = new Map();
    products.forEach((p) => {
      const key = p.region_label || p.region || p.region_slug || "Autres régions";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b, "fr"))
      .map(([label, items]) => ({
        label,
        items: items.sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr")),
      }));
  }, [products]);

  // Group courses: Published first, drafts last, alpha within each group.
  const coursesGrouped = useMemo(() => {
    const pub = courses.filter((c) => c.status === "published");
    const drafts = courses.filter((c) => c.status !== "published");
    const byTitle = (a, b) => (a.title || "").localeCompare(b.title || "", "fr");
    return [
      { label: "Parcours publiés", items: pub.sort(byTitle) },
      { label: "Brouillons", items: drafts.sort(byTitle) },
    ].filter((g) => g.items.length);
  }, [courses]);

  // Look up the display label for the auto-derived theme so operators
  // see the resolved appellation directly in the table, not just a slug.
  const themeLabelBySlug = useMemo(() => {
    const m = new Map();
    themes.forEach((t) => {
      m.set(t.slug, `${t.name}${t.region ? ` · ${t.region}` : ""}${t.country ? ` (${t.country})` : ""}`);
    });
    return m;
  }, [themes]);

  // Local datetime helpers — the <input type="datetime-local"> value is
  // "YYYY-MM-DDTHH:mm" in the browser's local tz. We store it as-is and
  // convert to a proper ISO UTC string only when we POST.
  const localToIso = (localDt) => {
    if (!localDt) return null;
    const d = new Date(localDt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };
  const nextFirstMondayLocal = (offsetMonths = 0) => {
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth() + 1 + offsetMonths, 1, 9, 0, 0);
    // Advance to the first Monday of that month (weekday 1).
    const shift = (1 - target.getDay() + 7) % 7;
    target.setDate(1 + shift);
    // Format as `YYYY-MM-DDTHH:mm` for datetime-local.
    const pad = (n) => String(n).padStart(2, "0");
    return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T09:00`;
  };

  const generate6 = () => {
    const next = [];
    for (let i = 0; i < 6; i += 1) {
      next.push({
        send_at: nextFirstMondayLocal(i),
        theme_slug: rows[i]?.theme_slug || "",
        product_id: rows[i]?.product_id || "",
        course_id: rows[i]?.course_id || "",
        narratives: rows[i]?.narratives || null,
        podcasts: rows[i]?.podcasts || [],
        feature_id: rows[i]?.feature_id || "",
        cepage_slugs: rows[i]?.cepage_slugs || [],
        producer_slugs: rows[i]?.producer_slugs || [],
        fiche_descriptions: rows[i]?.fiche_descriptions || {},
      });
    }
    setRows(next);
    setFlash("6 créneaux préremplis · choisissez un thème pour chaque ligne");
  };

  const addRow = () => {
    setRows((prev) => [...prev, {
      send_at: nextFirstMondayLocal(prev.length),
      theme_slug: "",
      product_id: "",
      course_id: "",
      narratives: null,
      podcasts: [],
      feature_id: "",
      cepage_slugs: [],
      producer_slugs: [],
      fiche_descriptions: {},
    }]);
  };
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const openPreview = async (idx) => {
    const row = rows[idx];
    if (!row?.product_id && !row?.theme_slug) {
      setFlash("Choisissez une carte (produit) avant de prévisualiser");
      return;
    }
    setPreviewIdx(idx);
    setPreviewLoading(true);
    setPreviewHtml("");
    try {
      // If we have a product but no theme yet, derive one first via /associate.
      let themeSlug = row.theme_slug;
      if (!themeSlug && row.product_id) {
        try {
          const a = await adminApi.newsletterThematicAssociate({
            product_id: row.product_id,
            course_id: row.course_id || undefined,
            lang: "fr",
          });
          themeSlug = a.theme_slug;
          updateRow(idx, { theme_slug: themeSlug });
        } catch (e) {
          throw new Error(e?.message || "Impossible de déduire un thème depuis cette carte");
        }
      }
      let html = "";
      if (row.narratives && Object.values(row.narratives).some((v) => v)) {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/thematic/preview`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            theme_slug: themeSlug,
            lang: "fr",
            product_id: row.product_id || undefined,
            course_id: row.course_id || undefined,
            narratives: row.narratives,
            podcasts: (row.podcasts || []).filter((p) => p && p.title && p.url),
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        html = await r.text();
      } else {
        const url = adminApi.newsletterThematicPreviewUrl(themeSlug, {
          product_id: row.product_id || undefined,
          with_ai: true,
        });
        const r = await fetch(url, { credentials: "include", headers: authHeaders() });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        html = await r.text();
      }
      setPreviewHtml(html);
    } catch (e) {
      setFlash(`Erreur aperçu : ${e?.message || e}`);
      setPreviewIdx(null);
    }
    setPreviewLoading(false);
  };

  // ── Rédaction (rich narrative editor) ─────────────────────────────────
  // NEW UX (2026-02): product + course are the primary drivers. When the
  // operator opens the editor we call `/associate` which derives the
  // theme_slug from the product, resolves the fiches, and returns 3 short
  // narrative pieces (intro / outro / app_feature).
  const openEditor = async (idx) => {
    const row = rows[idx];
    if (!row?.product_id) { setFlash("Choisissez d'abord une carte (produit) — c'est le focus de la newsletter"); return; }
    setEditIdx(idx);
    setEditPreviewHtml("");
    setEditBusy(true);
    try {
      // Always call `/associate` so we get the full theme + fiches list
      // to render the multi-select overrides — even when the row was
      // already edited (in which case we merge the operator's narratives
      // back in after the call).
      const d = await adminApi.newsletterThematicAssociate({
        product_id: row.product_id,
        course_id: row.course_id || undefined,
        lang: "fr",
        feature_id: row.feature_id || undefined,
        cepage_slugs: (row.cepage_slugs && row.cepage_slugs.length) ? row.cepage_slugs : undefined,
        producer_slugs: (row.producer_slugs && row.producer_slugs.length) ? row.producer_slugs : undefined,
      });
      // Preserve edited narratives — the associate call regenerated
      // them, but the operator's edits win when they exist.
      if (row.narratives && (row.narratives.intro || row.narratives.outro || row.narratives.app_feature)) {
        d.narratives = { ...d.narratives, ...row.narratives };
      }
      setEditDraft(d);
      // Adopt the derived theme_slug + feature_id into the row so
      // preview & save use them consistently.
      const patch = {};
      if (d.theme_slug && !row.theme_slug) patch.theme_slug = d.theme_slug;
      if (d.feature?.id && !row.feature_id) patch.feature_id = d.feature.id;
      if (Object.keys(patch).length) updateRow(idx, patch);
    } catch (e) {
      setFlash(`Erreur association : ${e?.message || e}`);
      setEditIdx(null);
    }
    setEditBusy(false);
  };

  const regenerateEditor = async () => {
    if (editIdx == null || !editDraft) return;
    setEditBusy(true);
    try {
      const d = await adminApi.newsletterThematicAssociate({
        product_id: rows[editIdx].product_id,
        course_id: rows[editIdx].course_id || undefined,
        lang: "fr",
        feature_id: rows[editIdx].feature_id || undefined,
        cepage_slugs: (rows[editIdx].cepage_slugs || []).length ? rows[editIdx].cepage_slugs : undefined,
        producer_slugs: (rows[editIdx].producer_slugs || []).length ? rows[editIdx].producer_slugs : undefined,
      });
      setEditDraft(d);
      setEditPreviewHtml("");
    } catch (e) {
      setFlash(`Erreur régénération : ${e?.message || e}`);
    }
    setEditBusy(false);
  };

  const previewEditor = async () => {
    if (!editDraft) return;
    setEditBusy(true);
    setEditPreviewHtml("");
    try {
      const row = rows[editIdx] || {};
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/thematic/preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          theme_slug: editDraft.theme_slug,
          lang: "fr",
          product_id: row.product_id || undefined,
          course_id: row.course_id || undefined,
          narratives: editDraft.narratives,
          podcasts: (row.podcasts || []).filter((p) => p && p.title && p.url),
          feature_id: row.feature_id || undefined,
          cepage_slugs: (row.cepage_slugs || []).length ? row.cepage_slugs : undefined,
          producer_slugs: (row.producer_slugs || []).length ? row.producer_slugs : undefined,
          fiche_descriptions: row.fiche_descriptions && Object.keys(row.fiche_descriptions).length
            ? row.fiche_descriptions : undefined,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setEditPreviewHtml(await r.text());
    } catch (e) {
      setFlash(`Erreur aperçu : ${e?.message || e}`);
    }
    setEditBusy(false);
  };

  const saveEditor = () => {
    if (editIdx == null || !editDraft) return;
    updateRow(editIdx, { narratives: editDraft.narratives });
    setEditIdx(null);
    setEditDraft(null);
    setEditPreviewHtml("");
    setFlash("Rédaction enregistrée pour cette ligne — n'oubliez pas « Enregistrer la planification » pour persister.");
  };

  const sendTest = async (idx) => {
    const row = rows[idx];
    const to = testEmailByRow[idx];
    if (!row?.theme_slug) { setFlash("Choisissez un thème avant l'envoi test"); return; }
    if (!to || !to.includes("@")) { setFlash("Email test invalide"); return; }
    setBusy(true);
    try {
      const r = await adminApi.newsletterThematicTestSend({
        theme_slug: row.theme_slug,
        to,
        lang: "fr",
        product_id: row.product_id || undefined,
        course_id: row.course_id || undefined,
        narratives: row.narratives || undefined,
        podcasts: (row.podcasts || []).filter((p) => p && p.title && p.url),
        feature_id: row.feature_id || undefined,
        cepage_slugs: (row.cepage_slugs || []).length ? row.cepage_slugs : undefined,
        producer_slugs: (row.producer_slugs || []).length ? row.producer_slugs : undefined,
        fiche_descriptions: row.fiche_descriptions && Object.keys(row.fiche_descriptions).length
          ? row.fiche_descriptions : undefined,
      });
      setFlash(`Test envoyé à ${r.sent_to} — sujet : "${r.subject}"`);
    } catch (e) {
      setFlash(`Erreur envoi test : ${e?.message || e}`);
    }
    setBusy(false);
  };

  const saveBulk = async () => {
    const items = rows
      .filter((r) => r.product_id && r.send_at)
      .map((r) => ({
        send_at: localToIso(r.send_at),
        theme_slug: r.theme_slug || null, // backend derives from product when missing
        product_id: r.product_id,
        course_id: r.course_id || null,
        narratives: r.narratives || null,
        podcasts: (r.podcasts || []).filter((p) => p && p.title && p.url),
        feature_id: r.feature_id || null,
        cepage_slugs: (r.cepage_slugs || []).length ? r.cepage_slugs : null,
        producer_slugs: (r.producer_slugs || []).length ? r.producer_slugs : null,
        fiche_descriptions: r.fiche_descriptions && Object.keys(r.fiche_descriptions).length
          ? r.fiche_descriptions : null,
      }));
    if (items.length === 0) { setFlash("Aucune ligne complète à enregistrer"); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Enregistrer ${items.length} newsletter${items.length > 1 ? "s" : ""} ? Les campagnes en file d'attente seront remplacées.`)) return;
    setBusy(true);
    try {
      const r = await adminApi.newsletterThematicScheduleBulk(items, true);
      setFlash(`${r.scheduled} planifiée(s)${r.errors?.length ? ` · ${r.errors.length} en erreur` : ""}`);
      const fresh = await adminApi.newsletterThematicList();
      setScheduled(fresh?.items || []);
    } catch (e) {
      setFlash(`Erreur : ${e?.message || e}`);
    }
    setBusy(false);
  };

  const cancel = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Annuler cette campagne programmée ?")) return;
    await adminApi.newsletterThematicCancel(id);
    const fresh = await adminApi.newsletterThematicList();
    setScheduled(fresh?.items || []);
  };
  const sendNow = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Envoyer maintenant à tous les abonnés actifs ?")) return;
    setBusy(true);
    try {
      const r = await adminApi.newsletterThematicSendNow(id);
      setFlash(`Envoyée — ${r.sent} destinataires (${r.errors} erreurs)`);
      const fresh = await adminApi.newsletterThematicList();
      setScheduled(fresh?.items || []);
    } catch (e) {
      setFlash(`Erreur : ${e?.message || e}`);
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="thematic-panel">
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[color:var(--gv-pink)]" />
            <h2 className="font-display text-xl text-[color:var(--gv-ink)]">Planification des newsletters</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={generate6}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] border border-[color:var(--gv-line)] text-xs hover:bg-[color:var(--gv-mist)]"
              data-testid="thematic-generate6"
            >
              <Calendar size={12} /> Générer les 6 prochaines
            </button>
            <button
              onClick={addRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] border border-[color:var(--gv-line)] text-xs hover:bg-[color:var(--gv-mist)]"
              data-testid="thematic-add-row"
            >
              <Plus size={12} /> Ajouter une ligne
            </button>
            <button
              onClick={saveBulk}
              disabled={busy || rows.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
              data-testid="thematic-save-bulk"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Enregistrer la planification
            </button>
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">
            Nouveau flow — carte + parcours en premier
          </label>
          <p className="text-[12px] text-[color:var(--gv-ink)]/70 leading-relaxed max-w-2xl">
            1. Choisissez la <strong>carte à vendre</strong> et le <strong>parcours Academy</strong> par ligne
            → l&apos;IA associe automatiquement l&apos;appellation, les cépages et les producteurs.
            2. Cliquez <em>Rédiger</em> pour générer 3 textes (intro, feature app du mois, outro) — modifiables.
            3. Ajoutez des podcasts, prévisualisez, envoyez un test, puis <em>Enregistrer la planification</em>.
          </p>
          <p className="text-[11px] text-[color:var(--gv-ink)]/50 mt-2">
            {themes.length} thèmes · {products.length} cartes · {courses.length} parcours Academy · signature Audrey Sousa Chaillet + IA + masthead <strong>✉️ La Lettre des Terroirs</strong>
          </p>
        </div>

        {rows.length === 0 ? (
          <div className="text-sm text-[color:var(--gv-ink)]/55 py-8 text-center border border-dashed border-[color:var(--gv-line)] rounded-[5px]">
            Cliquez « Générer les 6 prochaines » pour préremplir la planification.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="thematic-rows-table">
              <thead className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 text-left">
                <tr>
                  <th className="py-2 pr-2 w-10">#</th>
                  <th className="py-2 pr-2 w-[190px]">Date &amp; heure</th>
                  <th className="py-2 pr-2 w-[26%]">1. Carte à vendre</th>
                  <th className="py-2 pr-2 w-[24%]">2. Parcours Academy</th>
                  <th className="py-2 pr-2 w-[16%]">Thème (auto-associé)</th>
                  <th className="py-2 pr-2 w-[380px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} className="border-t border-[color:var(--gv-line)] align-top" data-testid={`thematic-row-${idx}`}>
                    <td className="py-2 pr-2 text-[color:var(--gv-ink)]/50">{idx + 1}</td>
                    <td className="py-2 pr-2">
                      <input
                        type="datetime-local"
                        value={r.send_at}
                        onChange={(e) => updateRow(idx, { send_at: e.target.value })}
                        className="w-full border border-[color:var(--gv-line)] rounded-[4px] px-2 py-1 text-xs"
                        data-testid={`thematic-datetime-${idx}`}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <select
                        value={r.product_id}
                        onChange={(e) => updateRow(idx, {
                          product_id: e.target.value,
                          // Reset the auto-derived theme when the product
                          // changes so the operator sees the association
                          // recompute on next "Rédiger" / preview.
                          theme_slug: e.target.value ? "" : r.theme_slug,
                          narratives: null,
                        })}
                        className="w-full border border-[color:var(--gv-line)] rounded-[4px] px-2 py-1 text-xs bg-white"
                        data-testid={`thematic-product-${idx}`}
                        title={products.find((p) => p.product_id === r.product_id)?.title || ""}
                      >
                        <option value="">— choisir une carte —</option>
                        {productsByRegion.map((g) => (
                          <optgroup key={g.label} label={g.label}>
                            {g.items.map((p) => (
                              <option key={p.product_id} value={p.product_id} title={p.title}>
                                {p.title}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <select
                        value={r.course_id}
                        onChange={(e) => updateRow(idx, { course_id: e.target.value, narratives: null })}
                        className="w-full border border-[color:var(--gv-line)] rounded-[4px] px-2 py-1 text-xs bg-white"
                        data-testid={`thematic-course-${idx}`}
                        title={courses.find((c) => c.id === r.course_id)?.title || ""}
                      >
                        <option value="">Auto (matché sur la carte)</option>
                        {coursesGrouped.map((g) => (
                          <optgroup key={g.label} label={g.label}>
                            {g.items.map((cx) => (
                              <option key={cx.id} value={cx.id} title={cx.title}>
                                {cx.title}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      {r.theme_slug ? (
                        <div className="text-[11.5px] px-2 py-1 rounded-[4px] bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] leading-snug">
                          <div className="text-[color:var(--gv-ink)] truncate" title={themeLabelBySlug.get(r.theme_slug) || r.theme_slug}>
                            {themeLabelBySlug.get(r.theme_slug) || r.theme_slug}
                          </div>
                          <button
                            type="button"
                            onClick={() => updateRow(idx, { theme_slug: "", narratives: null })}
                            className="text-[10.5px] text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-pink)] underline mt-0.5"
                            data-testid={`thematic-clear-theme-${idx}`}
                            title="Réinitialiser l'association pour la recalculer depuis la carte + parcours"
                          >
                            Réinitialiser
                          </button>
                        </div>
                      ) : (
                        <div className="text-[11px] text-[color:var(--gv-ink)]/45 italic px-2 py-1">
                          {r.product_id ? "Sera dérivé au clic sur Rédiger" : "—"}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex flex-wrap gap-1">
                        <button
                          onClick={() => openEditor(idx)}
                          disabled={!r.product_id || editBusy}
                          className={`text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 disabled:opacity-50 ${
                            r.narratives && Object.values(r.narratives).some((v) => v)
                              ? "bg-[color:var(--gv-pink)]/10 border-[color:var(--gv-pink)] text-[color:var(--gv-pink)]"
                              : "border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]"
                          }`}
                          data-testid={`thematic-edit-${idx}`}
                          title="Associer + rédiger (IA)"
                        >
                          <Sparkles size={11} /> Rédiger
                        </button>
                        <button
                          onClick={() => openPreview(idx)}
                          disabled={!r.product_id}
                          className="text-[11px] px-2 py-1 rounded border border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`thematic-preview-${idx}`}
                          title="Prévisualiser"
                        >
                          <FileText size={11} /> Aperçu
                        </button>
                        <input
                          type="email"
                          value={testEmailByRow[idx] || ""}
                          onChange={(e) => setTestEmailByRow((p) => ({ ...p, [idx]: e.target.value }))}
                          placeholder="email test"
                          className="text-[11px] border border-[color:var(--gv-line)] rounded px-2 py-1 w-32"
                          data-testid={`thematic-test-email-${idx}`}
                        />
                        <button
                          onClick={() => sendTest(idx)}
                          disabled={busy || !r.theme_slug}
                          className="text-[11px] px-2 py-1 rounded border border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`thematic-test-${idx}`}
                          title="Envoyer un email de test"
                        >
                          <Send size={11} /> Test
                        </button>
                        <button
                          onClick={() => removeRow(idx)}
                          className="text-[11px] px-2 py-1 rounded text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
                          data-testid={`thematic-remove-${idx}`}
                          title="Retirer cette ligne"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {flash && (
          <div className="mt-3 text-[13px] text-[color:var(--gv-ink)]/75 bg-[color:var(--gv-mist)] rounded px-3 py-2" data-testid="thematic-flash">
            {flash}
          </div>
        )}

        {previewIdx !== null && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewIdx(null)}>
            <div className="bg-white rounded-[8px] w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--gv-line)]">
                <div className="font-medium text-sm">
                  Aperçu — ligne {previewIdx + 1} · {rows[previewIdx]?.theme_slug}
                </div>
                <button onClick={() => setPreviewIdx(null)} className="p-1 text-slate-400 hover:text-slate-700">
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-auto">
                {previewLoading ? (
                  <div className="p-12 text-center">
                    <Loader2 size={20} className="animate-spin inline text-[color:var(--gv-pink)]" />
                    <div className="text-xs text-slate-500 mt-2">Génération de l&apos;intro et de la conclusion par IA…</div>
                  </div>
                ) : (
                  <iframe
                    srcDoc={previewHtml}
                    title="Aperçu newsletter"
                    className="w-full h-[720px]"
                    data-testid="thematic-preview-iframe"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rich narrative editor — 2-column: textareas left, live preview right */}
        {editIdx !== null && editDraft && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="thematic-editor-modal">
            <div className="bg-white rounded-[8px] w-full max-w-6xl h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--gv-line)]">
                <div>
                  <div className="font-medium text-sm">
                    Rédiger — ligne {editIdx + 1} · {editDraft.theme_slug}
                  </div>
                  {editDraft.suggested_product && (
                    <div className="text-[11px] text-[color:var(--gv-ink)]/55">
                      Carte suggérée : {editDraft.suggested_product.title?.slice(0, 80)}
                      {(editDraft.suggested_product.title?.length || 0) > 80 ? "…" : ""}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={regenerateEditor}
                    disabled={editBusy}
                    className="text-xs px-3 py-1.5 border border-[color:var(--gv-line)] rounded hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1"
                    data-testid="thematic-editor-regenerate"
                  >
                    <RotateCcw size={12} /> Régénérer par IA
                  </button>
                  <button
                    onClick={previewEditor}
                    disabled={editBusy}
                    className="text-xs px-3 py-1.5 border border-[color:var(--gv-line)] rounded hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1"
                    data-testid="thematic-editor-preview"
                  >
                    <FileText size={12} /> Aperçu
                  </button>
                  <button
                    onClick={saveEditor}
                    className="text-xs px-3 py-1.5 rounded bg-[color:var(--gv-pink)] text-white hover:opacity-90 inline-flex items-center gap-1"
                    data-testid="thematic-editor-save"
                  >
                    <Check size={12} /> Enregistrer
                  </button>
                  <button onClick={() => { setEditIdx(null); setEditDraft(null); setEditPreviewHtml(""); }} className="p-1 text-slate-400 hover:text-slate-700">
                    <X size={16} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2">
                {/* Left — textareas per narrative section */}
                <div className="overflow-y-auto p-5 border-r border-[color:var(--gv-line)] space-y-3">
                  {editBusy && !editDraft.narratives?.intro && (
                    <div className="text-center py-8">
                      <Loader2 size={20} className="animate-spin inline text-[color:var(--gv-pink)]" />
                      <div className="text-xs text-slate-500 mt-2">L&apos;IA rédige les 3 paragraphes…</div>
                    </div>
                  )}
                  {[
                    ["intro", "🎯 Intro — après le titre (le décor est planté)"],
                    ["app_feature", "📱 Feature app du mois (une fonctionnalité mise en avant)"],
                    ["outro", "🌿 Outro — avant la signature d'Audrey"],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">
                        {label}
                      </label>
                      <textarea
                        value={editDraft.narratives?.[key] || ""}
                        onChange={(e) => setEditDraft((d) => ({ ...d, narratives: { ...d.narratives, [key]: e.target.value } }))}
                        rows={4}
                        className="w-full border border-[color:var(--gv-line)] rounded px-2 py-1.5 text-[13px] leading-relaxed"
                        data-testid={`thematic-editor-${key}`}
                      />
                    </div>
                  ))}

                  {/* Podcasts editor — repeatable rows of {title, url, description} */}
                  <div className="mt-3 p-3 border border-[color:var(--gv-line)] rounded" data-testid="thematic-editor-podcasts">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">
                        🎧 Podcasts sur le thème ({(rows[editIdx]?.podcasts || []).length})
                      </label>
                      <button
                        onClick={() => {
                          const idx = editIdx;
                          updateRow(idx, { podcasts: [...(rows[idx]?.podcasts || []), { title: "", url: "", description: "" }] });
                        }}
                        className="text-[11px] px-2 py-1 rounded border border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1"
                        data-testid="thematic-podcast-add"
                      >
                        <Plus size={11} /> Ajouter un podcast
                      </button>
                    </div>
                    {(rows[editIdx]?.podcasts || []).length === 0 && (
                      <div className="text-[11.5px] text-[color:var(--gv-ink)]/50">
                        Aucun podcast — cliquez « Ajouter » pour insérer une section 🎧 dans l&apos;email.
                      </div>
                    )}
                    {(rows[editIdx]?.podcasts || []).map((p, pi) => (
                      <div key={pi} className="mb-3 pb-3 border-b border-[color:var(--gv-line)] last:border-b-0 last:mb-0 last:pb-0" data-testid={`thematic-podcast-row-${pi}`}>
                        <div className="grid grid-cols-2 gap-2 mb-1">
                          <input
                            value={p.title || ""}
                            onChange={(e) => {
                              const list = [...(rows[editIdx]?.podcasts || [])];
                              list[pi] = { ...list[pi], title: e.target.value };
                              updateRow(editIdx, { podcasts: list });
                            }}
                            placeholder="Titre de l'épisode"
                            className="border border-[color:var(--gv-line)] rounded px-2 py-1 text-[12.5px]"
                            data-testid={`thematic-podcast-title-${pi}`}
                          />
                          <input
                            value={p.url || ""}
                            onChange={(e) => {
                              const list = [...(rows[editIdx]?.podcasts || [])];
                              list[pi] = { ...list[pi], url: e.target.value };
                              updateRow(editIdx, { podcasts: list });
                            }}
                            placeholder="URL (Spotify, Ausha, YouTube…)"
                            className="border border-[color:var(--gv-line)] rounded px-2 py-1 text-[12.5px]"
                            data-testid={`thematic-podcast-url-${pi}`}
                          />
                        </div>
                        <div className="flex gap-2">
                          <input
                            value={p.description || ""}
                            onChange={(e) => {
                              const list = [...(rows[editIdx]?.podcasts || [])];
                              list[pi] = { ...list[pi], description: e.target.value };
                              updateRow(editIdx, { podcasts: list });
                            }}
                            placeholder="Description courte (optionnel)"
                            className="flex-1 border border-[color:var(--gv-line)] rounded px-2 py-1 text-[12.5px]"
                            data-testid={`thematic-podcast-desc-${pi}`}
                          />
                          <button
                            onClick={() => {
                              const list = [...(rows[editIdx]?.podcasts || [])];
                              list.splice(pi, 1);
                              updateRow(editIdx, { podcasts: list });
                            }}
                            className="text-rose-600 hover:bg-rose-50 rounded p-1"
                            data-testid={`thematic-podcast-remove-${pi}`}
                            title="Retirer ce podcast"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 border border-[color:var(--gv-line)] rounded" data-testid="thematic-editor-feature">
                    <label className="block text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">
                      📱 Feature app à mettre en avant
                    </label>
                    <select
                      value={rows[editIdx]?.feature_id || ""}
                      onChange={(e) => {
                        updateRow(editIdx, { feature_id: e.target.value });
                        // Optimistic UI update: reflect the new pick in the draft's `feature`
                        // block so operators see the change without re-running Régénérer.
                        const flat = (appFeatures || []).flatMap((g) => g.items);
                        const f = flat.find((x) => x.id === e.target.value);
                        if (f) setEditDraft((d) => ({ ...d, feature: f }));
                      }}
                      className="w-full border border-[color:var(--gv-line)] rounded px-2 py-1.5 text-[12.5px] bg-white"
                      data-testid="thematic-editor-feature-select"
                    >
                      <option value="">Auto — l&apos;IA choisit selon le thème</option>
                      {(appFeatures || []).map((g) => (
                        <optgroup key={g.module} label={g.module}>
                          {g.items.map((f) => (
                            <option key={f.id} value={f.id} title={f.description}>
                              {f.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {editDraft.feature && (
                      <div className="mt-2 text-[11.5px] text-[color:var(--gv-ink)]/70 leading-relaxed">
                        <div className="font-medium text-[color:var(--gv-ink)]">{editDraft.feature.name}</div>
                        <div className="mt-0.5">{editDraft.feature.description}</div>
                        <div className="mt-1 text-[color:var(--gv-pink)] font-medium">{editDraft.feature.price}</div>
                        <div className="mt-0.5 text-[color:var(--gv-ink)]/50 italic">
                          CTA : « {editDraft.feature.cta_label} » → {editDraft.feature.cta_path}
                        </div>
                      </div>
                    )}
                    <p className="mt-2 text-[10.5px] text-[color:var(--gv-ink)]/45 leading-snug">
                      Cliquez « Régénérer par IA » pour que l&apos;intro / le hook feature / l&apos;outro soient réécrits avec cette feature sélectionnée.
                    </p>
                  </div>

                  {/* Fiches manuellement éditables — cépages & producteurs.
                      Chaque fiche peut être retirée ou avoir sa description
                      écrasée avant l'envoi (ex: ajouter Gamay à Beaujolais). */}
                  <FichesEditor
                    editIdx={editIdx}
                    row={rows[editIdx]}
                    editDraft={editDraft}
                    updateRow={updateRow}
                    setEditDraft={setEditDraft}
                  />

                  <div className="mt-4 p-3 bg-[color:var(--gv-mist)] rounded text-[11.5px] text-[color:var(--gv-ink)]/70">
                    <strong>Contenu résolu automatiquement :</strong>
                    <div className="mt-1">Appellation : {editDraft.appellation?.name || "—"}</div>
                    <div>Cépages : {editDraft.cepages?.map((c) => c.name).join(" · ") || "aucun"}</div>
                    <div>Producteurs : {editDraft.producers?.map((p) => p.name).join(" · ") || "aucun"}</div>
                    <div>Parcours Academy : {editDraft.academy?.title || "aucun"}</div>
                  </div>
                </div>
                {/* Right — live preview iframe */}
                <div className="overflow-y-auto bg-[color:var(--gv-mist)]/40">
                  {editPreviewHtml ? (
                    <iframe
                      srcDoc={editPreviewHtml}
                      title="Aperçu newsletter"
                      className="w-full h-full min-h-[720px]"
                      data-testid="thematic-editor-preview-iframe"
                    />
                  ) : (
                    <div className="text-center py-24 text-[color:var(--gv-ink)]/55 text-sm">
                      <FileText size={20} className="inline mb-2" />
                      <div>Cliquez « Aperçu » pour visualiser le rendu final avec la signature d&apos;Audrey.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5">
        <h2 className="font-display text-xl text-[color:var(--gv-ink)] mb-4">Campagnes programmées</h2>
        {scheduled.length === 0 && (
          <div className="text-sm text-[color:var(--gv-ink)]/55">Aucune campagne dans la file.</div>
        )}
        <ul className="divide-y divide-[color:var(--gv-line)]">
          {scheduled.map((s) => (
            <li key={s.id} className="py-3 flex items-center justify-between gap-3" data-testid={`thematic-queued-${s.id}`}>
              <div className="text-[13.5px] text-[color:var(--gv-ink)]">
                <div className="font-medium">
                  {s.send_at ? new Date(s.send_at).toLocaleString("fr-FR") : s.send_date}
                  {" · "}
                  <MapPin size={12} className="inline mb-0.5" /> {s.theme_slug}
                  {s.lang && <span className="ml-2 text-[11px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[color:var(--gv-ink)]/8 text-[color:var(--gv-ink)]/70">{s.lang}</span>}
                  {s.source === "monthly-theme" && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-[color:var(--gv-pink)]/15 text-[color:var(--gv-pink)]">Thème du mois</span>}
                  {s.product_id && <span className="ml-2 text-[11px] text-[color:var(--gv-pink)]">carte forcée</span>}
                  {s.course_id && <span className="ml-2 text-[11px] text-[color:var(--gv-pink)]">parcours forcé</span>}
                  {(s.podcasts?.length || 0) > 0 && (
                    <span className="ml-2 text-[11px] text-[color:var(--gv-ink)]/60">🎧 {s.podcasts.length}</span>
                  )}
                </div>
                <div className="text-[color:var(--gv-ink)]/55 text-[12px]">
                  statut <strong>{s.status}</strong>
                  {s.fired_at && <> · envoyée {new Date(s.fired_at).toLocaleString("fr-FR")}</>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.open(adminApi.newsletterPreviewUrl(s.id), "_blank", "noopener,noreferrer")}
                  className="text-[12px] px-3 py-1.5 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1"
                  data-testid={`thematic-preview-${s.id}`}
                >
                  <FileText size={12} /> Prévisualiser
                </button>
                <button
                  onClick={async () => {
                    const addr = window.prompt("Envoyer un email de test à :", "audrey@geovinum.fr");
                    if (!addr) return;
                    try {
                      await adminApi.newsletterTestSend(s.id, addr);
                      window.alert(`Email test envoyé à ${addr}`);
                    } catch (e) {
                      window.alert(`Erreur: ${e.message}`);
                    }
                  }}
                  className="text-[12px] px-3 py-1.5 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1"
                  data-testid={`thematic-test-${s.id}`}
                >
                  <Mail size={12} /> Email test
                </button>
                {s.status === "queued" && (
                  <>
                    <button
                      onClick={() => sendNow(s.id)}
                      disabled={busy}
                      className="text-[12px] px-3 py-1.5 bg-[color:var(--gv-pink)] text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1"
                      data-testid={`thematic-send-${s.id}`}
                    >
                      <Send size={12} /> Publier
                    </button>
                    <button
                      onClick={() => cancel(s.id)}
                      className="text-[12px] px-3 py-1.5 text-rose-600 border border-rose-200 rounded-[4px] hover:bg-rose-50 inline-flex items-center gap-1"
                      data-testid={`thematic-cancel-${s.id}`}
                    >
                      <X size={12} /> Annuler
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Local helper — reads the admin auth token the same way `adminApi` does.
function authHeaders() {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem("geovinum.auth.v1") : null;
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

// ---------- Fiche autocomplete input ---------------------------------
// Search-as-you-type across name + region + country. Shows up to 12
// matches in a floating dropdown; keyboard nav (↑/↓/Enter) supported.
// Replaces the 200-option <select> that was unusable at scale.
function FicheAutocomplete({ label, pool, onFocus, onPick, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const rootRef = useRef(null);

  // Fuzzy contains — matches on name, region and country simultaneously.
  const matches = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    const arr = pool || [];
    if (!needle) return arr.slice(0, 12);
    const scored = [];
    for (const it of arr) {
      const name = (it.name || "").toLowerCase();
      const region = (it.region || it.origin || "").toLowerCase();
      const country = (it.country || "").toLowerCase();
      if (name.startsWith(needle)) scored.push({ it, s: 0 });
      else if (name.includes(needle)) scored.push({ it, s: 1 });
      else if (region.includes(needle)) scored.push({ it, s: 2 });
      else if (country.includes(needle)) scored.push({ it, s: 3 });
    }
    scored.sort((a, b) => a.s - b.s || (a.it.name || "").localeCompare(b.it.name || ""));
    return scored.slice(0, 12).map((x) => x.it);
  }, [pool, q]);

  // Close on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const commit = (item) => {
    if (!item) return;
    onPick(item.slug);
    setQ("");
    setOpen(false);
    setHover(0);
  };
  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); return; }
      return;
    }
    if (e.key === "ArrowDown") { setHover((h) => Math.min(h + 1, matches.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setHover((h) => Math.max(h - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter") { commit(matches[hover]); e.preventDefault(); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const placeholder = label.toLowerCase().startsWith("cép")
    ? `Rechercher un cépage (${(pool || []).length} disponibles) — nom ou région…`
    : `Rechercher un producteur (${(pool || []).length} disponibles) — nom ou région…`;

  return (
    <div className="relative" ref={rootRef} data-testid={testid}>
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHover(0); }}
        onFocus={() => { setOpen(true); onFocus && onFocus(); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="w-full border border-[color:var(--gv-line)] rounded px-2 py-1.5 text-[12px] bg-white pr-6"
        data-testid={`${testid}-input`}
      />
      <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/35 pointer-events-none" />
      {open && matches.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto border border-[color:var(--gv-line)] rounded bg-white shadow-lg" data-testid={`${testid}-list`}>
          {matches.map((m, i) => (
            <button
              key={m.slug}
              type="button"
              onMouseEnter={() => setHover(i)}
              onClick={() => commit(m)}
              className={`w-full text-left px-2 py-1.5 text-[12px] border-b border-[color:var(--gv-line)]/50 last:border-b-0 flex items-center justify-between gap-2 ${
                i === hover ? "bg-[color:var(--gv-mist)]" : "hover:bg-[color:var(--gv-mist)]/60"
              }`}
              data-testid={`${testid}-opt-${m.slug}`}
            >
              <span className="font-medium text-[color:var(--gv-ink)] truncate">{m.name}</span>
              {(m.region || m.country) && (
                <span className="text-[10.5px] text-[color:var(--gv-ink)]/55 truncate shrink-0 max-w-[55%]">
                  {m.region || m.country}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {open && q.trim() && matches.length === 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 px-2 py-2 border border-[color:var(--gv-line)] rounded bg-white text-[11.5px] text-[color:var(--gv-ink)]/55">
          Aucun résultat pour « {q} »
        </div>
      )}
    </div>
  );
}


// ---------- Fiches editor (cépages + producteurs manuellement pickés) ----------
// Renders the current AI-picked fiches with:
//   • a "retirer" button per row (removes the fiche from the send)
//   • an editable one-line description (overrides `_fiche_description()`)
//   • a search-add input to append a new fiche (ex: add Gamay to Beaujolais)
// The overrides sit on the ROW (cepage_slugs / producer_slugs /
// fiche_descriptions) so they survive schedule persistence and re-runs.
function FichesEditor({ editIdx, row, editDraft, updateRow }) {
  const [addingC, setAddingC] = useState("");
  const [addingP, setAddingP] = useState("");
  const [poolC, setPoolC] = useState([]); // full atlas cépages catalog
  const [poolP, setPoolP] = useState([]); // full atlas producers catalog
  const [loadingPools, setLoadingPools] = useState(false);
  const cepages = editDraft?.cepages || [];
  const producers = editDraft?.producers || [];
  const descs = row?.fiche_descriptions || {};

  // Lazy-load the full atlas pools the first time the operator wants to
  // add a fiche — avoids paying the cost on every editor open.
  const ensurePools = async () => {
    if (poolC.length || loadingPools) return;
    setLoadingPools(true);
    try {
      const [c, p] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/thematic/atlas?kind=cepages`,
              { credentials: "include", headers: authHeaders() }).then((r) => r.ok ? r.json() : null).catch(() => null),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/thematic/atlas?kind=producers`,
              { credentials: "include", headers: authHeaders() }).then((r) => r.ok ? r.json() : null).catch(() => null),
      ]);
      setPoolC((c?.items) || []);
      setPoolP((p?.items) || []);
    } catch { /* silent — the manual add just stays empty */ }
    setLoadingPools(false);
  };

  const setDesc = (slug, value) => {
    updateRow(editIdx, {
      fiche_descriptions: { ...(row?.fiche_descriptions || {}), [slug]: value },
    });
  };
  const removeCepage = (slug) => {
    const current = row?.cepage_slugs && row.cepage_slugs.length
      ? row.cepage_slugs
      : cepages.map((c) => c.slug);
    updateRow(editIdx, { cepage_slugs: current.filter((s) => s !== slug) });
  };
  const removeProducer = (slug) => {
    const current = row?.producer_slugs && row.producer_slugs.length
      ? row.producer_slugs
      : producers.map((p) => p.slug);
    updateRow(editIdx, { producer_slugs: current.filter((s) => s !== slug) });
  };
  const addCepage = (slug) => {
    if (!slug) return;
    const current = row?.cepage_slugs && row.cepage_slugs.length
      ? row.cepage_slugs
      : cepages.map((c) => c.slug);
    if (current.includes(slug)) return;
    updateRow(editIdx, { cepage_slugs: [...current, slug] });
    setAddingC("");
  };
  const addProducer = (slug) => {
    if (!slug) return;
    const current = row?.producer_slugs && row.producer_slugs.length
      ? row.producer_slugs
      : producers.map((p) => p.slug);
    if (current.includes(slug)) return;
    updateRow(editIdx, { producer_slugs: [...current, slug] });
    setAddingP("");
  };

  const renderList = (items, label, onRemove, adding, setAdding, onAdd, pool) => (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">{label}</label>
        <span className="text-[10.5px] text-[color:var(--gv-ink)]/45">{items.length} fiche{items.length > 1 ? "s" : ""}</span>
      </div>
      {items.map((it) => (
        <div key={it.slug} className="mb-2 p-2 border border-[color:var(--gv-line)] rounded bg-white" data-testid={`fiche-row-${it.slug}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-[12.5px] font-medium text-[color:var(--gv-ink)]">
              {it.name}
              {(it.region || it.origin) && (
                <span className="ml-1 text-[color:var(--gv-ink)]/55 font-normal">· {it.region || it.origin}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemove(it.slug)}
              className="text-rose-600 hover:bg-rose-50 rounded p-0.5"
              title="Retirer cette fiche"
              data-testid={`fiche-remove-${it.slug}`}
            >
              <Trash2 size={11} />
            </button>
          </div>
          <input
            value={descs[it.slug] ?? ""}
            onChange={(e) => setDesc(it.slug, e.target.value)}
            placeholder="Description courte (invitation au clic) — laissez vide pour utiliser l'auto"
            className="w-full border border-[color:var(--gv-line)] rounded px-2 py-1 text-[11.5px]"
            data-testid={`fiche-desc-${it.slug}`}
          />
        </div>
      ))}
      <div className="mt-1">
        <FicheAutocomplete
          label={label}
          pool={pool.filter((x) => !items.find((it) => it.slug === x.slug))}
          onFocus={ensurePools}
          onPick={(slug) => onAdd(slug)}
          testid={`fiche-add-${label.slice(0, 3).toLowerCase()}`}
        />
      </div>
    </div>
  );

  return (
    <div className="mt-4 p-3 border border-[color:var(--gv-line)] rounded" data-testid="thematic-editor-fiches">
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">
        🧩 Fiches éditables (retirer / ajouter / décrire)
      </div>
      {renderList(cepages, "Cépages", removeCepage, addingC, setAddingC, addCepage, poolC)}
      {renderList(producers, "Producteurs", removeProducer, addingP, setAddingP, addProducer, poolP)}
      <p className="text-[10.5px] text-[color:var(--gv-ink)]/45 leading-snug">
        Chaque fiche affiche son nom, sa région, la description (ci-dessus, ou auto) et une invitation « Découvrir la fiche → ».
      </p>
    </div>
  );
}

// ---------- Subscribers ----------
function SubscribersPanel() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ status: "active", lang: "", source: "", q: "" });
  const [loading, setLoading] = useState(true);
  // Bulk selection — a Set of subscriber IDs. `lastIdx` is the index of the
  // most recent single click, used as the anchor for shift-click range
  // selection (mimicking the finder / gmail multi-select convention).
  const [selected, setSelected] = useState(() => new Set());
  const [lastIdx, setLastIdx] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Add / import panels state (both closed by default).
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", lang: "fr", source: "manual", consent_text: "" });
  const [addBusy, setAddBusy] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvSource, setCsvSource] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvReport, setCsvReport] = useState(null);

  const load = () => {
    setLoading(true);
    setSelected(new Set());
    setLastIdx(null);
    adminApi.newsletterList(filters).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [filters.status, filters.lang, filters.source]);

  const revoke = (id) => {
    // eslint-disable-next-line
    if (window.confirm("Révoquer le consentement de cet abonné ?")) adminApi.newsletterRevoke(id).then(load);
  };

  const rows = data?.items || [];

  // Click handler for a row checkbox. Supports shift-click for range
  // selection: shift-click on row N with an anchor at row M toggles every
  // row between min(M,N) and max(M,N) to `checked`.
  const toggleRow = (idx, e) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const cur = rows[idx];
      if (!cur) return next;
      const shift = e?.shiftKey;
      if (shift && lastIdx !== null && lastIdx !== idx) {
        const [a, b] = lastIdx < idx ? [lastIdx, idx] : [idx, lastIdx];
        // Copy the "checked-ness" of the row we clicked into the range.
        const check = !next.has(cur.id);
        for (let i = a; i <= b; i += 1) {
          const r = rows[i];
          if (!r) continue;
          if (check) next.add(r.id); else next.delete(r.id);
        }
      } else if (next.has(cur.id)) {
        next.delete(cur.id);
      } else {
        next.add(cur.id);
      }
      return next;
    });
    setLastIdx(idx);
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer définitivement ${selected.size} abonné(s) ? Cette action est irréversible.`)) return;
    setBulkBusy(true);
    try {
      await adminApi.newsletterBulkDelete(Array.from(selected));
      load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Erreur : ${e?.message || e}`);
    }
    setBulkBusy(false);
  };

  // Softer counterpart — flips the row to `revoked` while preserving the
  // RGPD audit trail (consent_text + consent_at + horodatage `revoked_at`).
  // Preferred over bulk-delete for compliance.
  const bulkRevoke = async () => {
    if (selected.size === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Révoquer ${selected.size} abonné(s) ? Le consentement passe en "revoked" mais la ligne reste en base pour l'audit RGPD.`)) return;
    setBulkBusy(true);
    try {
      const r = await adminApi.newsletterBulkRevoke(Array.from(selected));
      // eslint-disable-next-line no-alert
      window.alert(`${r.revoked} abonné(s) révoqué(s).`);
      load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Erreur : ${e?.message || e}`);
    }
    setBulkBusy(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm" data-testid="filter-status">
          <option value="active">Actifs</option>
          <option value="revoked">Révoqués</option>
          <option value="all">Tous</option>
        </select>
        <select value={filters.lang} onChange={(e) => setFilters((f) => ({ ...f, lang: e.target.value }))}
                className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm" data-testid="filter-lang">
          <option value="">Toutes langues</option>
          <option value="fr">FR</option>
          <option value="en">EN</option>
          <option value="es">ES</option>
          <option value="pt">PT</option>
        </select>
        <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
                className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm" data-testid="filter-source">
          <option value="">Toutes sources</option>
          <option value="footer">Footer geovinum.io</option>
          <option value="app.geovinum.io">App geovinum.io</option>
          <option value="import">Import CSV</option>
          {/* Any additional source strings coming from `by_source` are rendered as extras. */}
          {Object.keys(data?.by_source || {})
            .filter((s) => s && !["footer", "app.geovinum.io", "import"].includes(s))
            .map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
               placeholder="Rechercher email…"
               onKeyDown={(e) => e.key === "Enter" && load()}
               className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm flex-1 min-w-[200px] max-w-xs" data-testid="filter-q" />
        <a href={adminApi.newsletterExportCsvUrl()} target="_blank" rel="noreferrer"
           className="btn-ghost text-xs" data-testid="export-csv">
          <Download size={13} /> Exporter CSV
        </a>
        <button
          onClick={async () => {
            // eslint-disable-next-line no-alert
            if (!window.confirm("Importer les abonnés newsletter depuis app.geovinum.io ? L'opération est idempotente — les doublons par email sont mergés.")) return;
            try {
              const r = await adminApi.newsletterImportApp("all");
              // eslint-disable-next-line no-alert
              window.alert(`Import terminé — ${r.fetched} récupérés · ${r.created} créés · ${r.updated} mis à jour · ${r.skipped} ignorés`);
              load();
            } catch (e) {
              // eslint-disable-next-line no-alert
              window.alert(`Erreur : ${e?.message || e}`);
            }
          }}
          className="btn-ghost text-xs"
          data-testid="import-app-btn"
          title="Récupérer les abonnés créés dans /account sur app.geovinum.io"
        >
          <RotateCcw size={13} /> Importer app.geovinum.io
        </button>
        <button
          onClick={() => { setAddOpen((v) => !v); setCsvOpen(false); }}
          className="btn-ghost text-xs"
          data-testid="add-manual-toggle"
          title="Ajouter un abonné manuellement"
        >
          <UserPlus size={13} /> Ajouter manuellement
        </button>
        <button
          onClick={() => { setCsvOpen((v) => !v); setAddOpen(false); setCsvReport(null); }}
          className="btn-ghost text-xs"
          data-testid="import-csv-toggle"
          title="Importer une liste depuis un CSV"
        >
          <Upload size={13} /> Importer CSV
        </button>
        <SpamPurgeButton scope="newsletter" onDone={load} />
      </div>

      {/* Manual add — inline form. */}
      {addOpen && (
        <div className="mb-4 p-4 border border-[color:var(--gv-line)] rounded-[5px] bg-white" data-testid="add-manual-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg">Ajouter un abonné</h3>
            <button onClick={() => setAddOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <input
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="email@exemple.com"
              className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm md:col-span-2"
              data-testid="add-manual-email"
            />
            <select
              value={addForm.lang}
              onChange={(e) => setAddForm((f) => ({ ...f, lang: e.target.value }))}
              className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm"
              data-testid="add-manual-lang"
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="pt">PT</option>
            </select>
            <input
              type="text"
              value={addForm.source}
              onChange={(e) => setAddForm((f) => ({ ...f, source: e.target.value }))}
              placeholder="source (ex: salon-vinexpo)"
              className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm"
              data-testid="add-manual-source"
            />
          </div>
          <textarea
            value={addForm.consent_text}
            onChange={(e) => setAddForm((f) => ({ ...f, consent_text: e.target.value }))}
            placeholder="Trace de consentement (RGPD) — ex : consentement recueilli sur formulaire papier au salon Vinexpo 2026."
            rows={2}
            className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm mb-3"
            data-testid="add-manual-consent"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                if (!addForm.email || !addForm.email.includes("@")) { window.alert("Email invalide"); return; }
                setAddBusy(true);
                try {
                  const r = await adminApi.newsletterAddManual({
                    email: addForm.email.trim(),
                    lang: addForm.lang,
                    source: addForm.source || "manual",
                    ...(addForm.consent_text ? { consent_text: addForm.consent_text } : {}),
                  });
                  window.alert(`${r.action === "created" ? "Créé" : "Mis à jour"} : ${r.email}`);
                  setAddForm({ email: "", lang: "fr", source: "manual", consent_text: "" });
                  setAddOpen(false);
                  load();
                } catch (e) {
                  window.alert(`Erreur : ${e?.message || e}`);
                } finally {
                  setAddBusy(false);
                }
              }}
              disabled={addBusy}
              className="btn-primary text-xs"
              data-testid="add-manual-submit"
            >
              {addBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Ajouter
            </button>
            <button onClick={() => setAddOpen(false)} className="btn-ghost text-xs">Annuler</button>
          </div>
        </div>
      )}

      {/* CSV import — inline uploader + report. */}
      {csvOpen && (
        <div className="mb-4 p-4 border border-[color:var(--gv-line)] rounded-[5px] bg-white" data-testid="import-csv-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg">Importer un CSV</h3>
            <button onClick={() => setCsvOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Colonnes attendues : <code className="font-mono text-xs bg-slate-50 px-1">email</code>, <code className="font-mono text-xs bg-slate-50 px-1">lang</code>, <code className="font-mono text-xs bg-slate-50 px-1">source</code>, <code className="font-mono text-xs bg-slate-50 px-1">consent_text</code>. Séparateur auto-détecté (, ; ou tab).
            Taille max 5 Mo. Les emails déjà présents sont mis à jour (idempotent).
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <a
              href={adminApi.newsletterCsvTemplateUrl()}
              className="btn-ghost text-xs"
              data-testid="csv-template-download"
            >
              <FileSpreadsheet size={13} /> Télécharger le modèle CSV
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input
              type="file"
              accept=".csv,text/csv,application/vnd.ms-excel"
              onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setCsvReport(null); }}
              className="text-sm md:col-span-2"
              data-testid="csv-file-input"
            />
            <input
              type="text"
              value={csvSource}
              onChange={(e) => setCsvSource(e.target.value)}
              placeholder="Forcer la source (optionnel)"
              className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm"
              data-testid="csv-source-override"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                if (!csvFile) { window.alert("Sélectionnez un fichier CSV"); return; }
                setCsvBusy(true);
                setCsvReport(null);
                try {
                  const r = await adminApi.newsletterImportCsv(csvFile, csvSource || undefined);
                  setCsvReport(r);
                  load();
                } catch (e) {
                  window.alert(`Erreur : ${e?.message || e}`);
                } finally {
                  setCsvBusy(false);
                }
              }}
              disabled={csvBusy || !csvFile}
              className="btn-primary text-xs"
              data-testid="csv-import-submit"
            >
              {csvBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              Importer
            </button>
            <button onClick={() => { setCsvOpen(false); setCsvReport(null); setCsvFile(null); }} className="btn-ghost text-xs">Fermer</button>
          </div>
          {csvReport && (
            <div className="mt-4 p-3 bg-slate-50 rounded text-sm space-y-2" data-testid="csv-import-report">
              <div className="flex flex-wrap gap-4">
                <span><strong>{csvReport.total_rows}</strong> lignes lues</span>
                <span className="text-emerald-700"><strong>{csvReport.created}</strong> créés</span>
                <span className="text-blue-700"><strong>{csvReport.updated}</strong> mis à jour</span>
                <span className="text-amber-700"><strong>{csvReport.skipped}</strong> ignorés</span>
              </div>
              {csvReport.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-red-700 text-xs">
                    Voir les {csvReport.errors.length} erreurs
                  </summary>
                  <ul className="mt-2 text-xs font-mono max-h-40 overflow-y-auto space-y-0.5">
                    {csvReport.errors.map((err, i) => (
                      <li key={i} className="text-slate-600">
                        Ligne {err.line} · {err.email} → <span className="text-red-700">{err.error}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-[color:var(--gv-ink)]/55"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Stat label="Total" value={data?.total ?? 0} />
            <Stat label="FR" value={data?.by_lang?.fr ?? 0} />
            <Stat label="Actifs" value={data?.by_status?.active ?? 0} />
            <Stat label="Révoqués" value={data?.by_status?.revoked ?? 0} />
          </div>
          {/* Bulk selection toolbar — visible only when at least 1 row is
              selected. Shows the count + "Delete N" action. */}
          {selected.size > 0 && (
            <div
              className="flex items-center gap-3 mb-3 bg-[color:var(--gv-ink)] text-white rounded-[5px] px-4 py-2.5"
              data-testid="bulk-toolbar"
            >
              <span className="text-sm">
                <strong>{selected.size}</strong> abonné{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setSelected(new Set())}
                className="text-[11px] text-white/70 hover:text-white"
                data-testid="bulk-clear"
              >
                Désélectionner
              </button>
              <div className="ml-auto flex gap-2">
                <button
                  onClick={bulkRevoke}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs px-3 py-1.5 rounded-[4px] disabled:opacity-50"
                  data-testid="bulk-revoke"
                >
                  <RotateCcw size={13} />
                  {bulkBusy ? "…" : `Révoquer ${selected.size}`}
                </button>
                <button
                  onClick={bulkDelete}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs px-3 py-1.5 rounded-[4px] disabled:opacity-50"
                  data-testid="bulk-delete"
                >
                  <Trash2 size={13} />
                  {bulkBusy ? "…" : `Supprimer ${selected.size}`}
                </button>
              </div>
            </div>
          )}
          <div className="text-[11.5px] text-[color:var(--gv-ink)]/50 mb-2">
            💡 Astuce : cochez le premier, <kbd className="px-1 py-0.5 border border-[color:var(--gv-line)] rounded text-[10px]">Maj</kbd>+clic sur le dernier pour tout sélectionner entre les deux.
          </div>
          <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-x-auto">
            <table className="w-full text-sm" data-testid="subscribers-table">
              <thead className="bg-[color:var(--gv-mist)] text-left text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selected.size === rows.length}
                      onChange={toggleAll}
                      aria-label="Sélectionner tout"
                      data-testid="bulk-toggle-all"
                    />
                  </th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Langue</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2">Statut</th>
                  <th className="px-4 py-2">Inscrit le</th>
                  <th className="px-4 py-2">Consentement</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s, idx) => (
                  <tr
                    key={s.id}
                    className={`border-t border-[color:var(--gv-line)] ${selected.has(s.id) ? "bg-[color:var(--gv-pink)]/5" : ""}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(s.id)}
                        onClick={(e) => toggleRow(idx, e)}
                        onChange={() => { /* handled in onClick above so shiftKey is available */ }}
                        aria-label={`Sélectionner ${s.email}`}
                        data-testid={`bulk-check-${s.id}`}
                      />
                    </td>
                    <td className="px-4 py-2 font-mono text-[12.5px]" data-testid={`row-email-${s.id}`}>{s.email}</td>
                    <td className="px-4 py-2 uppercase text-xs">{s.lang}</td>
                    <td className="px-4 py-2 text-xs text-[color:var(--gv-ink)]/65">{s.source}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${s.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-[color:var(--gv-ink)]/65">{(s.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2 text-[11px] text-[color:var(--gv-ink)]/70 max-w-[280px]" title={s.consent_text}>
                      {s.consent ? (
                        <span className="inline-flex items-center gap-1">
                          <Check size={12} className="text-emerald-600" />
                          <span className="font-medium text-emerald-700">RGPD</span>
                          <span className="text-[color:var(--gv-ink)]/55">
                            {s.consent_at
                              ? new Date(s.consent_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })
                              : ""}
                          </span>
                        </span>
                      ) : (
                        <span className="text-rose-600">✗ non validé</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {s.status === "active" && (
                        <button onClick={() => revoke(s.id)} className="text-xs text-[color:var(--gv-pink)] hover:underline"
                                data-testid={`revoke-${s.id}`}>
                          Révoquer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-[color:var(--gv-ink)]/55">Aucun abonné.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{label}</div>
      <div className="font-display text-xl text-[color:var(--gv-ink)]">{value}</div>
    </div>
  );
}

// ---------- Compose ----------
function ComposePanel() {
  const [content, setContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(true);
  const [campaign, setCampaign] = useState({
    subject: "", intro: "", items: [], lang: "fr", target_lang: "all", only_active: true,
  });
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    adminApi.newsletterContent().then(setContent).finally(() => setLoadingContent(false));
  }, []);

  const isPicked = (kind, id) => campaign.items.some((it) => it.kind === kind && it.id === id);
  const togglePick = (kind, id) => {
    setCampaign((c) => ({
      ...c,
      items: isPicked(kind, id)
        ? c.items.filter((it) => !(it.kind === kind && it.id === id))
        : [...c.items, { kind, id }],
    }));
  };

  const sendTest = () => {
    if (!testEmail) return alert("Renseigne une adresse email de test");
    if (!campaign.subject) return alert("Sujet manquant");
    setSending(true); setResult(null);
    adminApi.newsletterCampaignTest({
      subject: campaign.subject, intro: campaign.intro,
      items: campaign.items, lang: campaign.lang, test_email: testEmail,
    })
      .then((r) => setResult({ ok: true, text: `Email test envoyé à ${r.sent_to}` }))
      .catch((e) => setResult({ ok: false, text: e.message }))
      .finally(() => setSending(false));
  };

  const sendCampaign = () => {
    if (!campaign.subject) return alert("Sujet manquant");
    if (campaign.items.length === 0 && !window.confirm("Aucun item sélectionné — envoyer quand même ?")) return;
    if (!window.confirm(`Envoyer la campagne "${campaign.subject}" à tous les abonnés ${campaign.target_lang === "all" ? "(toutes langues)" : "(" + campaign.target_lang.toUpperCase() + ")"} ?`)) return;
    setSending(true); setResult(null);
    adminApi.newsletterCampaignSend(campaign)
      .then((r) => setResult({ ok: true, text: `✓ ${r.sent} email(s) envoyé(s).${r.errors?.length ? " " + r.errors.length + " erreur(s)." : ""}` }))
      .catch((e) => setResult({ ok: false, text: e.message }))
      .finally(() => setSending(false));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5">
          <h2 className="font-display text-lg text-[color:var(--gv-ink)] mb-4">1. Sujet et intro</h2>
          <input value={campaign.subject} onChange={(e) => setCampaign((c) => ({ ...c, subject: e.target.value }))}
                 placeholder="Sujet de la newsletter…"
                 className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm mb-3" data-testid="campaign-subject" />
          <textarea value={campaign.intro} onChange={(e) => setCampaign((c) => ({ ...c, intro: e.target.value }))}
                    placeholder="Petite intro (optionnel). Sera affichée tout en haut de la newsletter…"
                    rows={3}
                    className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm resize-y" data-testid="campaign-intro" />
        </div>

        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5">
          <h2 className="font-display text-lg text-[color:var(--gv-ink)] mb-4">2. Sélectionner les éléments à mettre en avant</h2>
          <p className="text-[12.5px] text-[color:var(--gv-ink)]/65 mb-4">
            Les éléments sélectionnés seront mis en forme automatiquement avec le template branding geoVINUM. Aucun aperçu visuel — utilise l&apos;envoi test pour vérifier.
          </p>
          {loadingContent ? (
            <div className="text-center py-10 text-[color:var(--gv-ink)]/55"><Loader2 size={18} className="animate-spin inline" /></div>
          ) : (
            <div className="space-y-5">
              <PickerSection label="Articles du blog" icon={<FileText size={14} />}
                              items={content?.blog || []} kind="blog" isPicked={isPicked} togglePick={togglePick} />
              <PickerSection label="Cartes & Posters (Shop)" icon={<ShoppingBag size={14} />}
                              items={content?.shop || []} kind="shop" isPicked={isPicked} togglePick={togglePick} />
              <PickerSection label="Académie" icon={<GraduationCap size={14} />}
                              items={content?.academy || []} kind="academy" isPicked={isPicked} togglePick={togglePick} />
            </div>
          )}
          <div className="mt-5 pt-4 border-t border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)]/65">
            <strong>{campaign.items.length}</strong> élément(s) sélectionné(s)
          </div>
        </div>
      </div>

      {/* Right side: send actions */}
      <div className="space-y-4">
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 sticky top-6">
          <h2 className="font-display text-lg text-[color:var(--gv-ink)] mb-4">3. Envoyer</h2>

          <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1.5 mt-3">Langue d&apos;envoi</label>
          <select value={campaign.target_lang} onChange={(e) => setCampaign((c) => ({ ...c, target_lang: e.target.value }))}
                  className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm mb-3" data-testid="campaign-target-lang">
            <option value="all">Toutes (langue de chaque abonné)</option>
            <option value="fr">FR uniquement</option>
            <option value="en">EN uniquement</option>
            <option value="es">ES uniquement</option>
            <option value="pt">PT uniquement</option>
          </select>

          <div className="border-t border-[color:var(--gv-line)] my-4 pt-4">
            <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1.5">Envoi de test</label>
            <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                   placeholder="adresse@test.com"
                   className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm mb-2" data-testid="campaign-test-email" />
            <button onClick={sendTest} disabled={sending || !campaign.subject || !testEmail}
                    className="btn-ghost w-full justify-center text-xs disabled:opacity-50" data-testid="campaign-test-send">
              {sending ? <Loader2 size={12} className="animate-spin" /> : <><Send size={12} /> Envoyer test</>}
            </button>
          </div>

          <div className="border-t border-[color:var(--gv-line)] mt-4 pt-4">
            <button onClick={sendCampaign} disabled={sending || !campaign.subject}
                    className="btn-primary w-full justify-center text-sm disabled:opacity-50" data-testid="campaign-send">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Envoyer la campagne</>}
            </button>
          </div>

          {result && (
            <div className={`mt-3 text-[12.5px] rounded p-2 ${result.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
                 data-testid="campaign-result">
              {result.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PickerSection({ label, icon, items, kind, isPicked, togglePick }) {
  if (!items.length) {
    return (
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">
          {icon} {label} <span className="text-[10px]">(0)</span>
        </div>
        <div className="text-xs text-[color:var(--gv-ink)]/45 italic">Aucun élément disponible.</div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">
        {icon} {label} <span className="text-[10px]">({items.length})</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
        {items.map((it) => {
          const picked = isPicked(kind, it.id);
          return (
            <button key={it.id} type="button" onClick={() => togglePick(kind, it.id)}
                    className={`text-left text-[12px] p-2 rounded-[5px] border transition-colors ${
                      picked
                        ? "bg-[color:var(--gv-pink)]/10 border-[color:var(--gv-pink)] text-[color:var(--gv-ink)]"
                        : "bg-white border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"
                    }`}
                    data-testid={`pick-${kind}-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="leading-tight">{it.title || it.id}</span>
                {picked ? <Check size={12} className="text-[color:var(--gv-pink)] shrink-0 mt-0.5" />
                        : <Plus size={12} className="text-[color:var(--gv-ink)]/40 shrink-0 mt-0.5" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- History ----------
function HistoryPanel() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    adminApi.newsletterCampaignsHistory().then((d) => setItems(d.items || []));
  }, []);
  if (!items) return <div className="text-center py-12 text-[color:var(--gv-ink)]/55"><Loader2 size={18} className="animate-spin inline" /></div>;
  if (!items.length) return <div className="text-center py-12 text-[color:var(--gv-ink)]/55">Aucune campagne envoyée.</div>;
  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-x-auto">
      <table className="w-full text-sm" data-testid="campaign-history">
        <thead className="bg-[color:var(--gv-mist)] text-left text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">
          <tr>
            <th className="px-4 py-2">Sujet</th>
            <th className="px-4 py-2">Langue cible</th>
            <th className="px-4 py-2">Envoyés</th>
            <th className="px-4 py-2">Erreurs</th>
            <th className="px-4 py-2">Date</th>
            <th className="px-4 py-2">Statut</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-t border-[color:var(--gv-line)]">
              <td className="px-4 py-2">{c.subject}</td>
              <td className="px-4 py-2 uppercase text-xs">{c.target_lang || c.lang}</td>
              <td className="px-4 py-2">{c.sent_count}</td>
              <td className="px-4 py-2 text-[color:var(--gv-pink)]">{c.error_count || 0}</td>
              <td className="px-4 py-2 text-xs">{(c.sent_at || c.created_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="px-4 py-2">
                <span className={`text-[11px] px-2 py-0.5 rounded ${c.status === "sent" ? "bg-green-100 text-green-700" : c.status === "sent_with_errors" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"}`}>
                  {c.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
